import React from "react";
import { Button, Skeleton, TextField } from "@mui/material";
import toast from "react-hot-toast";
import * as api from "../Api/index";
import CodeInput from "./CodeInput";
import BackupCodes from "./BackupCodes";
import "./TwoFactor.css";

const errorMessage = (error) =>
  error?.response?.data?.message || "Could not reach the server";

// The signed-in user's authenticator app. Hidden while an admin has the
// feature switched off.
export default function TwoFactorSettings() {
  const [status, setStatus] = React.useState(null);
  // "setup" | "disable" | "newCodes" while one of those forms is open
  const [mode, setMode] = React.useState(null);
  const [setup, setSetup] = React.useState(null);
  const [code, setCode] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [backupCodes, setBackupCodes] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const loadStatus = () =>
    api
      .getMyTwoFactor()
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus({ featureEnabled: false }));

  React.useEffect(() => {
    loadStatus();
  }, []);

  const close = () => {
    setMode(null);
    setSetup(null);
    setCode("");
    setPassword("");
  };

  const run = async (request, onSuccess) => {
    setBusy(true);
    try {
      const { data } = await request();
      onSuccess(data);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const startSetup = () =>
    run(api.startMyTwoFactorSetup, (data) => {
      setSetup(data);
      setMode("setup");
    });

  const confirmSetup = () =>
    run(
      () => api.enableMyTwoFactor(code),
      (data) => {
        close();
        setBackupCodes(data.backupCodes);
        toast.success("Authenticator app is set up");
        loadStatus();
      }
    );

  const disable = () =>
    run(
      () => api.disableMyTwoFactor({ password, code }),
      () => {
        close();
        toast.success("Authenticator app removed");
        loadStatus();
      }
    );

  const makeNewCodes = () =>
    run(
      () => api.newMyBackupCodes(code),
      (data) => {
        close();
        setBackupCodes(data.backupCodes);
        loadStatus();
      }
    );

  if (!status) {
    return (
      <div className="tf-panel tf-panel--card">
        <Skeleton variant="rounded" height={90} />
      </div>
    );
  }
  if (!status.featureEnabled) return null;

  return (
    <div className="tf-panel tf-panel--card">
      <div className="tf-header">
        <div>
          <h3 className="tf-heading">Authenticator App</h3>
          <p className="tf-text" style={{ marginBottom: 0 }}>
            Ask for a 6-digit code from an app on your phone (Google
            Authenticator, Microsoft Authenticator, Authy) each time you sign
            in, as well as your password.
          </p>
        </div>
        <span
          className={`tf-status ${
            status.enabled ? "tf-status--on" : "tf-status--off"
          }`}
        >
          {status.enabled ? "On" : "Off"}
        </span>
      </div>

      {backupCodes && (
        <div className="tf-section">
          <BackupCodes
            codes={backupCodes}
            onDone={() => setBackupCodes(null)}
            doneLabel="I have saved them"
          />
        </div>
      )}

      {!backupCodes && !mode && (
        <div className="tf-section">
          {status.enabled ? (
            <>
              <p className="tf-text">
                {status.backupCodesLeft} unused backup code
                {status.backupCodesLeft === 1 ? "" : "s"} left.
                {status.required &&
                  " Your bank requires the authenticator app, so it can't be turned off."}
              </p>
              <div className="tf-actions">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setMode("newCodes")}
                >
                  New backup codes
                </Button>
                {!status.required && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => setMode("disable")}
                  >
                    Turn off
                  </Button>
                )}
              </div>
            </>
          ) : (
            <div className="tf-actions">
              <Button
                size="small"
                variant="contained"
                disableElevation
                className="tf-primary"
                onClick={startSetup}
                disabled={busy}
              >
                Set up authenticator app
              </Button>
            </div>
          )}
        </div>
      )}

      {mode === "setup" && setup && (
        <div className="tf-section">
          <p className="tf-text">
            Scan this code with your authenticator app, then enter the 6-digit
            code it shows.
          </p>
          <div className="tf-qr">
            <img src={setup.qrCode} alt="QR code for your authenticator app" />
            <small>
              Can&apos;t scan? Enter this key in the app:
              <code>{setup.secret}</code>
            </small>
          </div>
          <CodeInput autoFocus value={code} onChange={setCode} />
          <div className="tf-actions">
            <Button size="small" onClick={close}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              disableElevation
              className="tf-primary"
              onClick={confirmSetup}
              disabled={busy || code.length !== 6}
            >
              Turn on
            </Button>
          </div>
        </div>
      )}

      {(mode === "disable" || mode === "newCodes") && (
        <div className="tf-section">
          {mode === "disable" && (
            <>
              <label className="tf-label" htmlFor="tf-password">
                Password
              </label>
              <TextField
                id="tf-password"
                fullWidth
                size="small"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                sx={{ mb: 1.5 }}
              />
            </>
          )}
          <span className="tf-label">Code from your authenticator app</span>
          <CodeInput
            autoFocus={mode === "newCodes"}
            value={code}
            onChange={setCode}
          />
          <div className="tf-actions">
            <Button size="small" onClick={close}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              disableElevation
              color={mode === "disable" ? "error" : "primary"}
              onClick={mode === "disable" ? disable : makeNewCodes}
              disabled={
                busy || code.length !== 6 || (mode === "disable" && !password)
              }
            >
              {mode === "disable" ? "Turn off" : "Make new codes"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
